/**
 * Scrolling promo message in the top bar.
 *
 * A two-copy CSS marquee only loops seamlessly when a single copy is wider than the visible
 * bar. The promo text is usually much shorter than that, so the track would run out and leave
 * a gap. This measures the bar and clones the text until the track is at least twice the
 * visible width, then animates it by exactly half the track - a whole number of copies, so the
 * loop has no seam.
 */
(function () {
  if (window.topBarMarqueeReady) return;
  window.topBarMarqueeReady = true;

  function build(marquee) {
    const track = marquee.querySelector('.top-bar__marquee-track');
    if (!track) return;

    // Always measure from a single copy, so a rebuild recalculates from scratch.
    const items = track.querySelectorAll('.top-bar__marquee-item');
    for (let i = 1; i < items.length; i++) items[i].remove();

    track.classList.remove('is-ready');
    track.style.removeProperty('--topbar-marquee-shift');
    track.style.removeProperty('--topbar-marquee-duration');

    const original = track.querySelector('.top-bar__marquee-item');
    if (!original) return;

    const itemWidth = original.getBoundingClientRect().width;
    const viewWidth = marquee.getBoundingClientRect().width;

    // Hidden or not laid out yet - nothing meaningful to measure.
    if (itemWidth < 1 || viewWidth < 1) return;

    // Enough copies to cover twice the bar, rounded up to an even number so that shifting
    // by half the track lands exactly on a copy boundary.
    let copies = Math.ceil((viewWidth * 2) / itemWidth);
    if (copies < 2) copies = 2;
    if (copies % 2 === 1) copies += 1;

    const fragment = document.createDocumentFragment();
    for (let i = 1; i < copies; i++) {
      const clone = original.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      fragment.appendChild(clone);
    }
    track.appendChild(fragment);

    // Treat the configured value as the time for one pass across the bar, so the text moves
    // at the same visible speed no matter how long it is or how many copies were needed.
    const secondsPerPass = parseFloat(track.dataset.duration) || 40;
    const shift = itemWidth * (copies / 2);
    const duration = shift / (viewWidth / secondsPerPass);

    track.style.setProperty('--topbar-marquee-shift', shift + 'px');
    track.style.setProperty('--topbar-marquee-duration', duration + 's');
    track.classList.add('is-ready');
  }

  function buildAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.top-bar__marquee').forEach(build);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => buildAll());
  } else {
    buildAll();
  }

  // The theme editor swaps the section's markup without reloading the page, so neither
  // DOMContentLoaded nor the section's own <script> tag fire again. Without this the
  // message would sit still and unrepeated after every settings change.
  document.addEventListener('shopify:section:load', (event) => buildAll(event.target));

  // Fonts load after DOMContentLoaded and change the measured width.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => buildAll());

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => buildAll(), 200);
  });
})();
